<template>
<div>
  <div class="postBlock">
    <div class="postAlign">
      <h3 class="form-group">{{getForumTitle}}</h3>
      <base-input v-if="!inWhoInSection" class="form-group" v-model="postTitle" :placeholder="$t('enterPostTitle')"></base-input>
      <div v-if="inWhoInSection">
        <base-select class="mb-4" v-model="whoInData.activity" :placeholder="$t('activity')" @option:selected="activityChange" valueProp="id" avoidDefaultSort :options="activitySource" searchable :label="normalizeForumProp('name')"></base-select>
        <base-input v-if="otherActivitySelected" class="form-group" v-model="whoInData.otherActivity" :placeholder="$t('insertActivityName')"></base-input>
        <base-select class="mb-4" v-model="whoInData.activityLevel" :placeholder="$t('level')" avoidDefaultSort valueProp="id" :options="activityLevelSource" searchable :label="normalizeForumProp('name')"></base-select>
        <base-input class="form-group" v-model="whoInData.participantsLimit" type="number" :min="2" :placeholder="`${$t('participantsLimit')} ${$t('optional')}`"></base-input>
        <b-row>
          <b-col class="mb-4" cols="12" xl="4"><base-select v-model="whoInData.country" :options="countrySource" :placeholder="$t('country')" @option:selected="countryChange" valueProp="idCountry" searchable :label="$i18nHelpers.normalizeProp('name')"></base-select></b-col>
          <b-col class="mb-4" cols="12" xl="3" offset-xl="1"><base-select v-model="whoInData.state" :options="stateSource" :placeholder="$t('state')" @option:selected="stateChange" valueProp="id" searchable :label="$i18nHelpers.normalizeProp('name')"></base-select></b-col>
          <b-col class="mb-4" cols="12" xl="3" offset-xl="1"><base-select v-model="whoInData.city" :options="citySource" :placeholder="$t('city')" valueProp="idCity" searchable :label="$i18nHelpers.normalizeProp('name')" :loading="false" :noOptionsTitle="$t('selectState')"></base-select></b-col>
        </b-row>
        <base-input class="form-group" v-model="whoInData.address" :placeholder="$t('address')"></base-input>
        <b-row>
          <b-col class="mb-4" cols="12" xl="4"><base-datepicker noMarginBottom dateAfterNow resetButtton v-model="startDate" :placeholder="`${$t('startDate')} ${$t('optional')}`"></base-datepicker></b-col>
          <b-col class="mb-4" cols="12" xl="3" offset-xl="1"><base-timepicker noMarginBottom buttonOnly v-model="startHour" :placeholder="`${$t('startHour')} ${$t('optional')}`"></base-timepicker></b-col>
        </b-row>
        <b-row>
          <b-col class="mb-4" cols="12" xl="4"><base-datepicker noMarginBottom dateAfterNow resetButtton v-model="endDate" :placeholder="`${$t('endDate')} ${$t('optional')}`"></base-datepicker></b-col>
          <b-col class="mb-4" cols="12" xl="3" offset-xl="1"><base-timepicker noMarginBottom buttonOnly v-model="endHour" :placeholder="`${$t('endHour')} ${$t('optional')}`"></base-timepicker></b-col>
        </b-row>
      </div>
      <div v-else>
        <b-form-file class="form-group" accept=".jpg, .jpeg, .png, .gif" v-model="postImage" :placeholder="$t('selectImage')" :multiple="false" :drop-placeholder="$t('drag-drop-file')" :browse-text="$t('browse')"></b-form-file>
        <v-select class="select-margin-bottom" v-if="showtagsSelector" multiple v-model="tags" @option:selected="updateTagsSource" @option:deselecting="tagDeselected($event)" @search="getTags" :placeholder="$t('mustEnterEntryTags')" :options="tagsSource" filtered searchable :label="normalizeForumProp('name')">
          <template #no-options="">
            {{$t('noTagsAvailables')}}
          </template>
        </v-select>
        <v-select class="select-margin-bottom" v-if="showCategorySelector" v-model="category" @search="getCategories" @option:selected="categorySelected" :placeholder="$t('mustEnterEntryCategory')" :options="categoriesSource" filtered searchable :label="normalizeForumProp('name')">
          <template #no-options="">
            {{$t('mustEnterEntryCategory')}}
          </template>
        </v-select>
        <v-select class="select-margin-bottom" v-if="showDiseaseInput" v-model="disease" taggable @option:created="inputNewDisease" :placeholder="$t('enterOrSearchDiseases')" :options="diseasesSource" filtered searchable label="name">
          <template #no-options="">
            {{$t('enterOrSearchDiseases')}}
          </template>
        </v-select>
      </div>
      <vue-editor :placeholder="inWhoInSection ? $t('commentaries') : $t('newPostDescription')" useCustomImageHandler @image-added="handleImageAdded" class="wysiwygStyle form-group" v-model="wysiwygInput"></vue-editor>
      <div style="margin-bottom: 10%; margin-top: 3%;">
        <base-button @click="sendForumForm">{{$t('send')}}</base-button>
        <base-button gray outline @click="back">{{$t('back')}}</base-button>
      </div>
    </div>
  </div>
</div>
</template>

<script>
import { BFormFile, BFormGroup } from 'bootstrap-vue'
import VueBootstrapTypeahead from 'vue-bootstrap-typeahead'
import BaseDatepicker from '@/components/base/forms/BaseDatepicker'
import BaseTimepicker from '@/components/base/forms/BaseTimepicker'
import { VueEditor } from 'vue2-editor'
import '@/plugins/vue-select'
import forumMixin from '@/mixins/forum.mixin'
import forumRepository from '@/repositories/ForumRepository'
import geographyRepository from '@/repositories/GeographyRepository'
import { WHO_IN_HEALTH_COMMUNITY_SECTION, HEALTHY_INFORMATION_HEALTH_COMMUNITY_SECTION, FORUM_HEALTH_COMMUNITY_SECTION, WHO_IN_SECTION_NAME, PATIENTS_LIKE_YOU_IN_SECTION_NAME } from '@/constants/healthyCommunitySections'
import { MX_COUNTRY_ID, SPAIN_COUNTRY_ID } from '@/constants/db/countries'
import { inMx } from '@/helpers/global.helpers'

const DISEASE_ID = '636255813ed3088c26324069'

export default {
  name: 'forum-new-post-form',
  components: {
    'base-input': () => import('@/components/base/forms/BaseInput').then(m => m.default),
    'base-button': () => import('@/components/base/buttons/BaseButton').then(m => m.default),
    BFormFile,
    VueBootstrapTypeahead,
    BFormGroup,
    BaseDatepicker,
    BaseTimepicker,
    VueEditor
  },
  props: {
  },
  data () {
    return {
        wysiwygInput: null,
        getPostNotifications: null,
        postTitle: null,
        postImage: null,
        category: null,
        tags: [],
        expirationDate: null,
        tagsSource: [],
        categoriesSource: [],
        tagsSourceFull: [],
        categoriesSourceFull: [],
        diseasesSource: [],
        showDiseaseInput: false,
        disease: null,
        sectionSource: [],
        section: null,
        activitySource: [],
        activityLevelSource: [],
        countrySource: [],
        stateSource: [],
        citySource: [],
        startDate: null,
        startHour: null,
        endDate: null,
        endHour: null,
        editingEntry: null,
        whoInData: {
          country: inMx ? MX_COUNTRY_ID : SPAIN_COUNTRY_ID,
          state: null,
          city: null,
          address: null,
          activity: null,
          otherActivity: null,
          activityLevel: null,
          startDate: null,
          endDate: null,
          commentaries: null,
          participantsLimit: null,
          noStartTime: null,
          noEndTime: null
        }
    }
  },
  created () {
    this.$store.state.loading = false
    if (this.inWhoInSection) {
      this.getActivities()
      this.getActivitiesLevel()
      this.editingEntry = null
      if (this.$store.state.forum.editingEntry) {
        this.editingEntry = true
        this.$store.state.forum.editingEntry = false
        this.fillWhoInData()
      } else {
        this.getCountries()
        this.getStates()
      }
    } else {
      this.$store.state.forum.sectionId = FORUM_HEALTH_COMMUNITY_SECTION
      this.getTags()
      this.getCategories()
      this.getSections()
    }
  },
  mixins: [forumMixin],
  computed: {
    getForumTitle () {
      if (this.inWhoInSection) {
        return this.$t('newActivity')
      } else if (this.showSectionSelector() && !this.section) {
        return this.$t('newPostTitle', [''])
      } else if (this.showSectionSelector() && this.section) {
        return this.$t('newPostTitle', [this.getForumSectionTitle(this.section.id, true)])
      } else {
        return this.$t('newPostTitle', [this.getForumSectionTitle(this.$store.state.forum.sectionId, true)])
      }
    },
    otherActivitySelected () {
      return this.whoInData.activity === -1
    },
    showCategorySelector () {
      return (!this.section && (this.$store.state.forum.sectionId === HEALTHY_INFORMATION_HEALTH_COMMUNITY_SECTION || this.$store.state.forum.sectionId === FORUM_HEALTH_COMMUNITY_SECTION)) || (this.section && (this.section.id === HEALTHY_INFORMATION_HEALTH_COMMUNITY_SECTION || this.section.id === FORUM_HEALTH_COMMUNITY_SECTION))
    },
    showtagsSelector () {
      return (!this.section && this.$store.state.forum.sectionId === FORUM_HEALTH_COMMUNITY_SECTION) || (this.section && this.section.id === FORUM_HEALTH_COMMUNITY_SECTION)
    }
  },
  methods: {
    sendForumForm () {
      if (this.inWhoInSection) {
        this.sendWhoInData()
      } else {
        this.sendForumData()
      }
    },
    back () {
      window.history.back()
    },
    sendWhoInData () {
      if (this.startHour && !this.startDate) {
        this.$store.dispatch('showDangerAlert', this.$t('startDateNotSelected'))
        return
      } else if (this.startDate && !this.startHour) {
        this.$store.dispatch('showDangerAlert', this.$t('startHourNotSelected'))
        return
      } else if (this.startDate && this.startHour) {
        this.whoInData.startDate = new Date(`${this.startDate}${this.startHour ? `T${this.startHour}` : ''}`)
        this.whoInData.noStartTime = !this.startHour
      } else {
        this.whoInData.startDate = null
      }
      if (this.endHour && !this.endDate) {
        this.$store.dispatch('showDangerAlert', this.$t('endDateNotSelected'))
        return
      } else if (this.endDate && !this.endHour) {
        this.$store.dispatch('showDangerAlert', this.$t('endHourNotSelected'))
        return
      } else if (this.endDate && this.endHour) {
        this.whoInData.endDate = new Date(`${this.endDate}${this.endHour ? `T${this.endHour}` : ''}`)
        this.whoInData.noEndTime = !this.endHour
      } else {
        this.whoInData.endDate = null
      }
      if (this.wysiwygInput) this.whoInData.commentaries = this.wysiwygInput
      if (this.postTitle) this.whoInData.title = this.postTitle
      if (!this.whoInData.activity) {
        this.$store.dispatch('showDangerAlert', this.$t('mustSelectActivity'))
      } else if (this.whoInData.activity === -1 && !this.whoInData.otherActivity) {
        this.$store.dispatch('showDangerAlert', this.$t('mustInsertOtherActivity'))
      } else if (!this.whoInData.activityLevel) {
        this.$store.dispatch('showDangerAlert', this.$t('mustSelectActivityLevel'))
      } else if (!this.whoInData.state) {
        this.$store.dispatch('showDangerAlert', this.$t('mustSelectState'))
      } else if (!this.whoInData.city) {
        this.$store.dispatch('showDangerAlert', this.$t('mustSelectCity'))
      } else if (!this.whoInData.address) {
        this.$store.dispatch('showDangerAlert', this.$t('mustSelectAddress'))
      } else if (this.whoInData.startDate && (this.whoInData.startDate < new Date())) {
        this.$store.dispatch('showDangerAlert', this.$t('dateStartGreaterThanToday'))
      } else if (this.whoInData.endDate && (this.whoInData.endDate < new Date())) {
        this.$store.dispatch('showDangerAlert', this.$t('dateEndGreaterThanToday'))
      } else if ((this.whoInData.startDate && this.whoInData.endDate && (this.whoInData.startDate > this.whoInData.endDate)) || (!this.whoInData.startDate && this.whoInData.endDate)) {
        this.$store.dispatch('showDangerAlert', this.$t('dateEndGreaterThanStart'))
      } else if (this.whoInData.participantsLimit && (isNaN(this.whoInData.participantsLimit) || this.whoInData.participantsLimit < 2)) {
        this.$store.dispatch('showDangerAlert', this.$t('participantsLimitGreaterThan2'))
      } else if (this.editingEntry && this.whoInData.participantsLimit && this.whoInData.participantsLimit < this.$store.state.forum.entryEditData.whoInData.participants.length) {
        this.$store.dispatch('showDangerAlert', this.$t('participantsLimitLowerThenCurrentParticipants', {participants: this.$store.state.forum.entryEditData.whoInData.participants.length}))
      } else {
        forumRepository[this.editingEntry ? 'editWhoInEntry' : 'sendWhoInData'](this.whoInData).then(data => {
          if (data) {
            this.$store.dispatch('showSuccessAlert', this.editingEntry ? this.$t('activityUpdatedCorrectly') : this.$t('postPublishedCorrectly'))
            this.$router.push(this.getHealthyCommunityPathUrl(WHO_IN_SECTION_NAME))
          } else {
            this.$store.dispatch('showDangerAlert', this.$t('defaultErrorMessage'))
          }
        })
      }
    },
    sendForumData () {
      if (!this.postTitle) {
        this.$store.dispatch('showDangerAlert', this.$t('emptyTitleError'))
      } else if (this.expirationDate && new Date(this.expirationDate) <= Date.now()) {
        this.$store.dispatch('showDangerAlert', this.$t('dateMustBeGreaterThanNow'))
      } else if (this.showCategorySelector && !this.category) {
        this.$store.dispatch('showDangerAlert', this.$t('entryCategoryNotSelected'))
      } else if (this.showCategorySelector && this.category.id === DISEASE_ID && !this.disease) {
        this.$store.dispatch('showDangerAlert', this.$t('mustSelectDisease'))
      } else if (!this.wysiwygInput) {
        this.$store.dispatch('showDangerAlert', this.$t('emptyForumContentError'))
      } else {
        let data = {title: this.postTitle.trim(), description: this.wysiwygInput, tags: this.tags, category: this.category, disease: this.disease, section: this.section, expirationDate: this.expirationDate}
        let responseMessage = (data) => {
          if (data) {
            this.$store.dispatch('showSuccessAlert', this.$store.getters['forum/forumEntryWithoutValidation'] ? this.$t('postPublishedCorrectly') : this.$t('postPublishedCorrectlyPending'))
            this.$router.push(this.getHealthyCommunityPathUrl(PATIENTS_LIKE_YOU_IN_SECTION_NAME))
          } else {
            this.$store.dispatch('showDangerAlert', this.$t('defaultErrorMessage'))
          }
        }
        if (this.postImage) {
          this.$fileHelpers.uploadFile('/Forum/CreateEntry', data, this.postImage, this).then((response) => {
            responseMessage(response.data)
          })
        } else {
          this.$api.post('/Forum/CreateEntry', data).then(response => {
            responseMessage(response.data)
          })
        }
      }
    },
    getCategories (search, loading) {
      if (search) {
        loading(true)
        this.categoriesSource = this.categoriesSourceFull.filter(item => item.name.toLowerCase().indexOf(search.toLowerCase()) > -1)
        loading(false)
      } else if (this.categoriesSourceFull.length === 0) {
        this.$api.get('Forum/SearchEntryCategory').then(response => {
          this.categoriesSourceFull = response.data
          this.categoriesSource = response.data
        })
      }
    },
    getDiseases () {
      this.$api.get('Forum/SearchEntryDisease').then(response => {
          this.diseasesSource = response.data
        })
    },
    getSections () {
      this.$api.get('Forum/GetEntrySection').then(response => {
        if (response) {
          this.sectionSource = response.data
          if (this.$store.state.forum.sectionId === WHO_IN_HEALTH_COMMUNITY_SECTION || this.$store.state.forum.sectionId === HEALTHY_INFORMATION_HEALTH_COMMUNITY_SECTION || this.$store.state.forum.sectionId === FORUM_HEALTH_COMMUNITY_SECTION) {
            this.section = response.data.find(item => item.id === this.$store.state.forum.sectionId)
          }
        }
      })
    },
    inputNewDisease (disease) {
      this.disease = {id: null, name: disease.name}
    },
    categorySelected (item) {
      if (item.id === DISEASE_ID) {
        this.getDiseases()
        this.showDiseaseInput = true
      } else {
        this.showDiseaseInput = false
        this.disease = null
      }
    },
    getTags (search, loading) {
      if (search) {
        loading(true)
        this.tagsSource = this.tagsSourceFull.filter(item => item.name.toLowerCase().indexOf(search.toLowerCase()) > -1)
        loading(false)
      } else if (this.tagsSourceFull.length === 0) {
        this.$api.get('Forum/SearchEntryTag').then(response => {
          this.tagsSourceFull = response.data
          this.tagsSource = response.data
        })
      }
    },
    activityChange () {
      if (this.whoInData.activity !== -1) this.whoInData.otherActivity = null
    },
    getActivities () {
      forumRepository.getActivities().then(response => {
        response.data.push({ id: -1, name: this.$t('otherActivity'), nameEn: this.$t('otherActivity'), namePt: this.$t('otherActivity') })
        this.activitySource = response.data
      })
    },
    getActivitiesLevel () {
      forumRepository.getActivitiesLevel().then(response => {
        this.activityLevelSource = response.data
      })
    },
    getCountries () {
      geographyRepository.getCountries().then(response => {
				this.countrySource = response.data
			})
    },
    getStates () {
      geographyRepository.getStatesByCountry(this.whoInData.country).then(response => {
				this.stateSource = response.data
			})
    },
    getCities () {
      geographyRepository.getStateCities(this.whoInData.state).then(response => {
				this.citySource = response.data
			})
    },
    countryChange () {
      this.getStates()
      this.citySource = []
      this.whoInData.state = null
      this.whoInData.city = null
    },
    stateChange () {
      this.whoInData.city = null
      this.getCities()
    },
    updateTagsSource () {
      this.tagsSourceFull = this.tagsSourceFull.filter(item => {
          return !this.tags.some(x => x.id === item.id)
      })
      this.tagsSource = this.tagsSourceFull
    },
    tagDeselected (tag) {
      this.tagsSourceFull.push(tag)
      this.tagsSourceFull.sort((a, b) => a.name.localeCompare(b.name))
      this.tagsSource = this.tagsSourceFull
    },
    showSectionSelector () {
      return location.href.indexOf('comunidadsaludable') > -1 && this.$store.state.forum.userBasicInfo.companyCode && (this.$store.state.forum.sectionId === FORUM_HEALTH_COMMUNITY_SECTION || !this.$store.state.forum.sectionId)
    },
    sectionSelected () {
      if (this.section.id === WHO_IN_HEALTH_COMMUNITY_SECTION) {
        this.wysiwygInput = this.$t('whoInNewPost')
      } else {
        this.wysiwygInput = null
      }
    },
    fillWhoInData () {
      let data = this.$store.state.forum.entryEditData.whoInData
      const keysToExclude = ['participants', 'noStartTime', 'noEndTime']
      const activityKey = 'activity'
      this.whoInData.id = this.$store.state.forum.entryEditData.id
      Object.keys(data).filter(key => !keysToExclude.includes(key)).forEach(key => {
        if (data[key]) {
          if (key === activityKey && data[key].createdByUser) {
            this.whoInData.activity = -1
            this.whoInData.otherActivity = data[key][this.normalizeForumProp('name')]
          } else {
            this.whoInData[key] = this.getWhoInPropValue(data, key)
          }
        }
      })
      this.getCountries()
      this.getStates()
      this.getCities()
    },
    getWhoInPropValue (data, key) {
      const startDateKey = 'startDate'
      const endDateKey = 'endDate'
      const noStartTimeKey = 'noStartTime'
      const noEndTimeKey = 'noEndTime'
      const commentariesKey = 'commentaries'
      const value = data[key]

      if (typeof value === 'object') {
        return value.id
      } else if (!isNaN(new Date(value))) {
        const date = new Date(value)
        if (key === startDateKey) {
          this.startDate = date.toISOString().split('T')[0]
          if (!data[noStartTimeKey]) this.startHour = date.toLocaleTimeString()
        } else if (key === endDateKey) {
          this.endDate = date.toISOString().split('T')[0]
          if (!data[noEndTimeKey]) this.endHour = date.toLocaleTimeString()
        }
        return value
      } else if (key === commentariesKey) {
        this.wysiwygInput = value
        return value
      } else {
        return value
      }
    }
  },
  mounted () {
  }
}
</script>

<style lang="scss">
.alignLeft {
    text-align: left;
}
.marginAllSide {
    margin: 1%;
}
.postBlock {
  display: flex;
  flex-direction: column;
  align-items: center;
}
.postAlign {
  width: 80vw; 
  vertical-align: baseline;
  max-width: 1140px;
}
 @include media(mobile, tablets) {
  .postAlign {
    width: 90vw;
  } 
}
div.select-margin-bottom {
  margin-bottom: 1.5rem !important;
}
</style>


// WEBPACK FOOTER //
// src/components/views/healthy-community/forum/ForumNewPostForm.vue